import React, { useState, useEffect, useRef } from 'react';
import projectManager from '../../models/projects';
import { useWorkspaceRoster, useWorkspaceRosterMap } from '../../hooks/useWorkspaceRoster';
import { useCurrentUser } from '../../contexts/CurrentUserContext';
import { api } from '../../api/client';

const NAVY = '#0C2D48';
const BORDER = '#e0e0e0';
const BG = '#f8f9fb';

const STATUS_OPTIONS = ['todo', 'in progress', 'completed'];
const PRIORITY_OPTIONS = ['Low', 'Medium', 'High'];

const STATUS_COLOR = { 'todo': '#a4b0be', 'in progress': '#5352ed', 'completed': '#27ae60' };
const PRIORITY_COLOR = { 'Low': '#7bed9f', 'Medium': '#ffa502', 'High': '#ff4757' };

function FileIcon({ source, mimeType }) {
  if (source === 'google_drive') return (
    <svg width="18" height="18" viewBox="0 0 87.3 78" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M6.6 66.85l3.85 6.65c.8 1.4 1.95 2.5 3.3 3.3L27.5 53H0c0 1.55.4 3.1 1.2 4.5L6.6 66.85z" fill="#0066da"/>
      <path d="M43.65 25L29.9 1.2C28.55.4 27 0 25.45 0c-1.55 0-3.1.4-4.5 1.2L6.6 11.15 27.5 47h32.3L43.65 25z" fill="#00ac47"/>
      <path d="M73.55 76.8c1.35-.8 2.5-1.9 3.3-3.3l1.6-2.75 7.65-13.25c.8-1.4 1.2-2.95 1.2-4.5H59.8l6.45 12.4 7.3 11.4z" fill="#ea4335"/>
      <path d="M43.65 25L57.4 1.2C56.05.4 54.5 0 52.95 0H34.35c-1.55 0-3.1.4-4.45 1.2L43.65 25z" fill="#00832d"/>
      <path d="M59.8 53H27.5L13.75 76.8c1.35.8 2.9 1.2 4.45 1.2h50.9c1.55 0 3.1-.4 4.45-1.2L59.8 53z" fill="#2684fc"/>
      <path d="M73.4 27.5L59.65 3.85C58.85 2.45 57.75 1.35 56.4.55L43.65 25l16.15 28H87.3c0-1.55-.4-3.1-1.2-4.5L73.4 27.5z" fill="#ffba00"/>
    </svg>
  );
  if (source === 'onedrive') return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M19.5 11.5a4.5 4.5 0 0 0-4.25-4.49A6 6 0 0 0 3.75 10.5a3.75 3.75 0 0 0 .75 7.5h15a3 3 0 0 0 0-6.5z" fill="#0078d4"/>
    </svg>
  );
  if (source === 'url') return <span style={{ fontSize: 16 }}>🔗</span>;
  if (mimeType?.startsWith('image/')) return <span style={{ fontSize: 16 }}>🖼️</span>;
  if (mimeType === 'application/pdf') return <span style={{ fontSize: 16 }}>📄</span>;
  return <span style={{ fontSize: 16 }}>📎</span>;
}

function Avatar({ userId, size = 26 }) {
  // Resolves from the shared roster cache — no per-avatar fetch, no mock lookup.
  const rosterMap = useWorkspaceRosterMap();
  const u = rosterMap.get(userId);
  const name = u?.name || 'Former member';
  return (
    <div
      title={name}
      style={{
        width: size, height: size, borderRadius: '50%',
        background: u?.color || '#9ca3af', color: '#fff',
        fontSize: size * 0.42, fontWeight: 700,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      {name.charAt(0)}
    </div>
  );
}

function Chip({ label, color, onRemove }) {
  return (
    <span
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 4,
        padding: '2px 9px', borderRadius: 20,
        background: `${color}25`, color, fontSize: 11, fontWeight: 700,
        border: `1px solid ${color}50`,
      }}
    >
      {label}
      {onRemove && (
        <button
          onClick={onRemove}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color, lineHeight: 1, padding: 0, fontSize: 13 }}
        >
          ×
        </button>
      )}
    </span>
  );
}

export default function TaskDetailPanel({ taskId, onClose, onUpdated }) {
  const { currentUser } = useCurrentUser();
  // Roster hooks must run before any early return (rules-of-hooks).
  const { members: allUsers } = useWorkspaceRoster();
  const rosterMap = useWorkspaceRosterMap();
  const [task, setTask] = useState(null);
  const [tab, setTab] = useState('details');
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState('');
  const [editingDesc, setEditingDesc] = useState(false);
  const [descDraft, setDescDraft] = useState('');

  // Comments
  const [commentText, setCommentText] = useState('');

  // Time log
  const [logMins, setLogMins] = useState('');
  const [logNote, setLogNote] = useState('');

  // Subtasks
  const [subtaskTitle, setSubtaskTitle] = useState('');
  const [subtasks, setSubtasks] = useState([]);

  // Files
  const [attachments, setAttachments] = useState([]);
  const [addingFile, setAddingFile] = useState(false);
  const [newFileName, setNewFileName] = useState('');
  const [newFileUrl, setNewFileUrl] = useState('');
  const [savingFile, setSavingFile] = useState(false);
  const [linkingFile, setLinkingFile] = useState(false);
  const [linkSource, setLinkSource] = useState('google_drive');
  const [linkName, setLinkName] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [savingLink, setSavingLink] = useState(false);

  // Tags
  const [tagInput, setTagInput] = useState('');

  const titleInputRef = useRef(null);

  const reload = () => {
    if (!taskId) return;
    const t = projectManager.getTaskById(taskId);
    if (t) {
      setTask(t);
      setSubtasks(projectManager.getSubtasks(taskId));
    }
  };

  useEffect(() => {
    if (!taskId) { setTask(null); return; }
    setTab('details');
    reload();
    loadAttachments();
  }, [taskId]); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadAttachments() {
    try {
      const res = await api(`/api/tasks/${taskId}/attachments`);
      const data = await res.json();
      if (Array.isArray(data.attachments)) setAttachments(data.attachments);
    } catch { /* ignore */ }
  }

  async function addAttachment() {
    if (!newFileName.trim() || !newFileUrl.trim()) return;
    setSavingFile(true);
    try {
      const res = await api(`/api/tasks/${taskId}/attachments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fileName: newFileName.trim(), fileUrl: newFileUrl.trim(), source: 'upload' }),
      });
      const data = await res.json();
      if (data.attachment) {
        setAttachments(prev => [data.attachment, ...prev]);
        setNewFileName('');
        setNewFileUrl('');
        setAddingFile(false);
      }
    } catch { /* ignore */ } finally {
      setSavingFile(false);
    }
  }

  async function addLink() {
    if (!linkName.trim() || !linkUrl.trim()) return;
    setSavingLink(true);
    try {
      const res = await api(`/api/tasks/${taskId}/attachments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fileName: linkName.trim(), externalUrl: linkUrl.trim(), source: linkSource }),
      });
      const data = await res.json();
      if (data.attachment) {
        setAttachments(prev => [data.attachment, ...prev]);
        setLinkName('');
        setLinkUrl('');
        setLinkingFile(false);
      }
    } catch { /* ignore */ } finally {
      setSavingLink(false);
    }
  }

  async function removeAttachment(attachId) {
    try {
      await api(`/api/tasks/${taskId}/attachments/${attachId}`, { method: 'DELETE' });
      setAttachments(prev => prev.filter(a => a.id !== attachId));
    } catch (err) {
      console.error('Failed to remove attachment:', err);
    }
  }

  // Private attachments (storageKey set) have no public URL — fetch a short-lived
  // pre-signed URL on demand and open it. Legacy/external rows keep their href.
  async function openAttachment(a) {
    try {
      const res = await api(`/api/tasks/${taskId}/attachments/${a.id}/url`);
      const data = await res.json();
      if (data.url) window.open(data.url, '_blank', 'noopener');
    } catch (err) {
      console.error('Failed to open attachment:', err);
    }
  }

  useEffect(() => {
    if (editingTitle && titleInputRef.current) titleInputRef.current.focus();
  }, [editingTitle]);

  if (!taskId || !task) return null;

  const update = (changes) => {
    projectManager.updateProjectTask(taskId, changes);
    reload();
    onUpdated && onUpdated();
  };

  const handleTitleSave = () => {
    if (titleDraft.trim()) update({ title: titleDraft.trim() });
    setEditingTitle(false);
  };

  const handleDescSave = () => {
    update({ description: descDraft });
    setEditingDesc(false);
  };

  const handleAddComment = () => {
    if (!commentText.trim()) return;
    projectManager.addComment(taskId, currentUser?.id || 1, commentText.trim());
    setCommentText('');
    reload();
  };

  const handleLogTime = () => {
    const mins = parseInt(logMins);
    if (!mins || mins < 1) return;
    projectManager.logTime(taskId, currentUser?.id || 1, mins, logNote.trim());
    setLogMins('');
    setLogNote('');
    reload();
  };

  const handleCreateSubtask = async () => {
    if (!subtaskTitle.trim()) return;
    await projectManager.createSubtask(taskId, { title: subtaskTitle.trim(), priority: 'Medium', assignedTo: [] });
    setSubtaskTitle('');
    reload();
  };

  const handleSubtaskToggle = (subId, completed) => {
    projectManager.updateProjectTask(subId, { status: completed ? 'completed' : 'todo' });
    reload();
  };

  const addTag = () => {
    const t = tagInput.trim().toLowerCase();
    if (!t || (task.tags || []).includes(t)) { setTagInput(''); return; }
    update({ tags: [...(task.tags || []), t] });
    setTagInput('');
  };

  const removeTag = (tag) => update({ tags: (task.tags || []).filter(x => x !== tag) });

  const toggleAssignee = (userId) => {
    const current = task.assignedTo || [];
    update({
      assignedTo: current.includes(userId)
        ? current.filter(x => x !== userId)
        : [...current, userId],
    });
  };

  const totalTime = (task.timeTracked || []).reduce((s, e) => s + e.minutes, 0);
  const formatTime = (m) => {
    const h = Math.floor(m / 60);
    const min = m % 60;
    return h > 0 ? `${h}h ${min}m` : `${min}m`;
  };

  const project = projectManager.getProject(task.projectId);

  const PANELW = 480;

  return (
    <>
      {/* Overlay */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed', inset: 0,
          background: 'rgba(0,0,0,0.15)',
          zIndex: 1400,
        }}
      />

      {/* Panel */}
      <div
        style={{
          position: 'fixed',
          top: 0, right: 0, bottom: 0,
          width: PANELW,
          maxWidth: '100vw',
          background: '#fff',
          borderLeft: `1px solid ${BORDER}`,
          zIndex: 1500,
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '-8px 0 40px rgba(0,0,0,0.12)',
        }}
      >
        {/* Panel header */}
        <div style={{
          padding: '16px 20px',
          borderBottom: `1px solid ${BORDER}`,
          display: 'flex', alignItems: 'flex-start', gap: 12,
        }}>
          <div style={{ flex: 1 }}>
            {editingTitle ? (
              <input
                ref={titleInputRef}
                value={titleDraft}
                onChange={e => setTitleDraft(e.target.value)}
                onBlur={handleTitleSave}
                onKeyDown={e => { if (e.key === 'Enter') handleTitleSave(); if (e.key === 'Escape') setEditingTitle(false); }}
                style={{
                  width: '100%', fontSize: 16, fontWeight: 700, color: NAVY,
                  border: 'none', borderBottom: `2px solid ${NAVY}`,
                  outline: 'none', background: 'transparent', padding: '2px 0',
                  boxSizing: 'border-box',
                }}
              />
            ) : (
              <h2
                onClick={() => { setTitleDraft(task.title); setEditingTitle(true); }}
                style={{
                  margin: 0, fontSize: 16, fontWeight: 700, color: NAVY,
                  cursor: 'text', lineHeight: 1.3,
                }}
                title="Click to edit"
              >
                {task.title}
              </h2>
            )}
            {project && (
              <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
                <span
                  style={{
                    display: 'inline-block',
                    width: 8, height: 8, borderRadius: '50%',
                    background: project.color, marginRight: 5,
                  }}
                />
                {project.name}
              </div>
            )}
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none', border: 'none',
              fontSize: 22, cursor: 'pointer', color: '#999',
              lineHeight: 1, flexShrink: 0, padding: 0,
            }}
          >
            ×
          </button>
        </div>

        {/* Quick status + priority row */}
        <div style={{
          padding: '12px 20px',
          borderBottom: `1px solid ${BORDER}`,
          display: 'flex', gap: 8, flexWrap: 'wrap',
        }}>
          {/* Status */}
          <select
            value={task.status}
            onChange={e => update({ status: e.target.value })}
            style={{
              padding: '5px 10px', borderRadius: 20,
              border: `1px solid ${STATUS_COLOR[task.status]}`,
              background: `${STATUS_COLOR[task.status]}18`,
              color: STATUS_COLOR[task.status], fontSize: 12, fontWeight: 700,
              cursor: 'pointer', outline: 'none',
            }}
          >
            {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
          </select>

          {/* Priority */}
          <select
            value={task.priority}
            onChange={e => update({ priority: e.target.value })}
            style={{
              padding: '5px 10px', borderRadius: 20,
              border: `1px solid ${PRIORITY_COLOR[task.priority] || BORDER}`,
              background: `${PRIORITY_COLOR[task.priority] || '#ddd'}18`,
              color: PRIORITY_COLOR[task.priority] || '#555', fontSize: 12, fontWeight: 700,
              cursor: 'pointer', outline: 'none',
            }}
          >
            {PRIORITY_OPTIONS.map(p => <option key={p} value={p}>{p}</option>)}
          </select>

          {/* Due date */}
          <input
            type="date"
            value={task.dueDate || ''}
            onChange={e => update({ dueDate: e.target.value })}
            style={{
              padding: '5px 10px', borderRadius: 20,
              border: `1px solid ${BORDER}`,
              fontSize: 12, cursor: 'pointer', outline: 'none',
              background: '#fff', color: '#555',
            }}
          />

          {/* Story points */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <span style={{ fontSize: 11, color: '#888', fontWeight: 600 }}>SP</span>
            <input
              type="number"
              value={task.storyPoints || ''}
              onChange={e => update({ storyPoints: parseInt(e.target.value) || 0 })}
              min="0"
              style={{
                width: 42, padding: '5px 6px', borderRadius: 8,
                border: `1px solid ${BORDER}`, fontSize: 12, textAlign: 'center', outline: 'none',
              }}
            />
          </div>
        </div>

        {/* Tabs */}
        <div style={{
          display: 'flex', borderBottom: `1px solid ${BORDER}`,
          padding: '0 20px',
        }}>
          {['details', 'subtasks', 'comments', 'time', 'files'].map(t => (
            <button
              key={t}
              onClick={() => setTab(t)}
              style={{
                padding: '10px 14px',
                border: 'none', background: 'none',
                fontSize: 13, fontWeight: tab === t ? 700 : 400,
                color: tab === t ? NAVY : '#888',
                borderBottom: tab === t ? `2px solid ${NAVY}` : '2px solid transparent',
                cursor: 'pointer', marginBottom: -1, textTransform: 'capitalize',
              }}
            >
              {t === 'time' ? 'Time Log' : t === 'files' ? `Files${attachments.length > 0 ? ` (${attachments.length})` : ''}` : t}
              {t === 'subtasks' && subtasks.length > 0 && (
                <span style={{
                  marginLeft: 5, fontSize: 10, background: NAVY, color: '#fff',
                  borderRadius: 10, padding: '1px 5px', fontWeight: 700,
                }}>
                  {subtasks.length}
                </span>
              )}
              {t === 'comments' && (task.comments || []).length > 0 && (
                <span style={{
                  marginLeft: 5, fontSize: 10, background: '#5352ed', color: '#fff',
                  borderRadius: 10, padding: '1px 5px', fontWeight: 700,
                }}>
                  {(task.comments || []).length}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Tab content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '18px 20px' }}>

          {/* ── DETAILS ── */}
          {tab === 'details' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
              {/* Description */}
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#999', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}>
                  Description
                </div>
                {editingDesc ? (
                  <div>
                    <textarea
                      value={descDraft}
                      onChange={e => setDescDraft(e.target.value)}
                      autoFocus
                      style={{
                        width: '100%', minHeight: 90, resize: 'vertical',
                        border: `1px solid ${NAVY}`, borderRadius: 8,
                        padding: '9px 12px', fontSize: 13, outline: 'none',
                        boxSizing: 'border-box',
                      }}
                    />
                    <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                      <button onClick={handleDescSave} style={{ padding: '5px 14px', borderRadius: 6, border: 'none', background: NAVY, color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>Save</button>
                      <button onClick={() => setEditingDesc(false)} style={{ padding: '5px 14px', borderRadius: 6, border: `1px solid ${BORDER}`, background: '#fff', fontSize: 12, cursor: 'pointer' }}>Cancel</button>
                    </div>
                  </div>
                ) : (
                  <p
                    onClick={() => { setDescDraft(task.description || ''); setEditingDesc(true); }}
                    style={{
                      margin: 0, fontSize: 13, color: task.description ? '#333' : '#bbb',
                      lineHeight: 1.6, cursor: 'text',
                      padding: '8px 10px', borderRadius: 8,
                      border: `1px dashed transparent`,
                      minHeight: 36,
                    }}
                    title="Click to edit"
                  >
                    {task.description || 'Add a description…'}
                  </p>
                )}
              </div>

              {/* Assignees */}
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#999', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 8 }}>
                  Assignees
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {allUsers.map(u => {
                    const assigned = (task.assignedTo || []).includes(u.id);
                    return (
                      <button
                        key={u.id}
                        onClick={() => toggleAssignee(u.id)}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 5,
                          padding: '4px 10px', borderRadius: 20,
                          border: `2px solid ${assigned ? u.color : BORDER}`,
                          background: assigned ? `${u.color}18` : '#fff',
                          cursor: 'pointer', fontSize: 12, fontWeight: 600,
                          color: assigned ? u.color : '#777',
                        }}
                      >
                        <Avatar userId={u.id} size={18} />
                        {u.name}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Tags */}
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#999', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 8 }}>
                  Tags
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
                  {(task.tags || []).map(tag => (
                    <Chip key={tag} label={tag} color="#5352ed" onRemove={() => removeTag(tag)} />
                  ))}
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <input
                    value={tagInput}
                    onChange={e => setTagInput(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && addTag()}
                    placeholder="Add tag…"
                    style={{
                      flex: 1, padding: '6px 10px', borderRadius: 8,
                      border: `1px solid ${BORDER}`, fontSize: 12, outline: 'none',
                    }}
                  />
                  <button
                    onClick={addTag}
                    style={{
                      padding: '6px 12px', borderRadius: 8,
                      border: `1px solid ${BORDER}`, background: BG,
                      fontSize: 12, cursor: 'pointer', fontWeight: 700, color: NAVY,
                    }}
                  >
                    Add
                  </button>
                </div>
              </div>

              {/* Summary stats */}
              <div style={{
                display: 'flex', gap: 14, padding: '12px 14px',
                background: BG, borderRadius: 10, border: `1px solid ${BORDER}`,
              }}>
                <StatItem label="Time logged" value={formatTime(totalTime)} />
                <StatItem label="Story points" value={task.storyPoints || 0} />
                <StatItem label="Subtasks" value={subtasks.length} />
                <StatItem label="Comments" value={(task.comments || []).length} />
              </div>
            </div>
          )}

          {/* ── SUBTASKS ── */}
          {tab === 'subtasks' && (
            <div>
              <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
                <input
                  value={subtaskTitle}
                  onChange={e => setSubtaskTitle(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleCreateSubtask()}
                  placeholder="Add subtask…"
                  style={{
                    flex: 1, padding: '8px 12px', borderRadius: 8,
                    border: `1px solid ${BORDER}`, fontSize: 13, outline: 'none',
                  }}
                />
                <button
                  onClick={handleCreateSubtask}
                  style={{
                    padding: '8px 14px', borderRadius: 8,
                    border: 'none', background: NAVY, color: '#fff',
                    fontSize: 13, fontWeight: 700, cursor: 'pointer',
                  }}
                >
                  Add
                </button>
              </div>

              {subtasks.length === 0 ? (
                <p style={{ color: '#bbb', fontSize: 13, textAlign: 'center', marginTop: 32 }}>No subtasks yet.</p>
              ) : (
                subtasks.map(sub => (
                  <div
                    key={sub.id}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      padding: '9px 12px', marginBottom: 4,
                      borderRadius: 8, border: `1px solid ${BORDER}`,
                      background: sub.status === 'completed' ? `${BORDER}50` : '#fff',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={sub.status === 'completed'}
                      onChange={e => handleSubtaskToggle(sub.id, e.target.checked)}
                      style={{ cursor: 'pointer', width: 14, height: 14 }}
                    />
                    <span
                      style={{
                        flex: 1, fontSize: 13, color: sub.status === 'completed' ? '#aaa' : '#333',
                        textDecoration: sub.status === 'completed' ? 'line-through' : 'none',
                      }}
                    >
                      {sub.title}
                    </span>
                    <span style={{
                      fontSize: 10, fontWeight: 700, color: '#888',
                      background: BORDER, borderRadius: 8, padding: '2px 6px',
                    }}>
                      {sub.status}
                    </span>
                  </div>
                ))
              )}
            </div>
          )}

          {/* ── COMMENTS ── */}
          {tab === 'comments' && (
            <div>
              {/* Add comment */}
              <div style={{ marginBottom: 16 }}>
                <textarea
                  value={commentText}
                  onChange={e => setCommentText(e.target.value)}
                  placeholder="Write a comment… (@mention teammates)"
                  style={{
                    width: '100%', minHeight: 72, resize: 'vertical',
                    border: `1px solid ${BORDER}`, borderRadius: 8,
                    padding: '9px 12px', fontSize: 13, outline: 'none',
                    boxSizing: 'border-box',
                  }}
                />
                <button
                  onClick={handleAddComment}
                  style={{
                    marginTop: 6, padding: '7px 16px', borderRadius: 8,
                    border: 'none', background: NAVY, color: '#fff',
                    fontSize: 13, fontWeight: 700, cursor: 'pointer',
                  }}
                >
                  Post Comment
                </button>
              </div>

              {(task.comments || []).length === 0 ? (
                <p style={{ color: '#bbb', fontSize: 13, textAlign: 'center', marginTop: 20 }}>No comments yet.</p>
              ) : (
                [...(task.comments || [])].reverse().map(c => {
                  const author = rosterMap.get(c.userId) || { name: 'Former member', color: '#9ca3af' };
                  return (
                    <div
                      key={c.id}
                      style={{
                        display: 'flex', gap: 10, marginBottom: 14,
                        padding: '10px 12px', borderRadius: 10, background: BG,
                        border: `1px solid ${BORDER}`,
                      }}
                    >
                      {author && (
                        <div
                          style={{
                            width: 30, height: 30, borderRadius: '50%',
                            background: author.color, color: '#fff',
                            fontSize: 13, fontWeight: 700, flexShrink: 0,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                          }}
                        >
                          {author.name.charAt(0)}
                        </div>
                      )}
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', marginBottom: 4 }}>
                          <span style={{ fontSize: 13, fontWeight: 700, color: NAVY }}>
                            {author?.name || 'Unknown'}
                          </span>
                          <span style={{ fontSize: 11, color: '#aaa' }}>{c.date}</span>
                        </div>
                        <p style={{ margin: 0, fontSize: 13, color: '#333', lineHeight: 1.5 }}>{c.comment}</p>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}

          {/* ── TIME LOG ── */}
          {tab === 'time' && (
            <div>
              {/* Total */}
              <div style={{
                padding: '10px 14px', borderRadius: 10,
                background: `${NAVY}10`, border: `1px solid ${NAVY}30`,
                marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: NAVY }}>Total logged</span>
                <span style={{ fontSize: 18, fontWeight: 800, color: NAVY }}>{formatTime(totalTime)}</span>
              </div>

              {/* Log form */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 20, padding: '12px 14px', border: `1px solid ${BORDER}`, borderRadius: 10 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#555', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Log Time</div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <div style={{ flex: 1 }}>
                    <input
                      type="number"
                      value={logMins}
                      onChange={e => setLogMins(e.target.value)}
                      min="1"
                      placeholder="Minutes"
                      style={{
                        width: '100%', padding: '8px 10px', borderRadius: 8,
                        border: `1px solid ${BORDER}`, fontSize: 13, outline: 'none',
                        boxSizing: 'border-box',
                      }}
                    />
                  </div>
                  <input
                    value={logNote}
                    onChange={e => setLogNote(e.target.value)}
                    placeholder="What did you work on?"
                    style={{
                      flex: 2, padding: '8px 10px', borderRadius: 8,
                      border: `1px solid ${BORDER}`, fontSize: 13, outline: 'none',
                    }}
                  />
                </div>
                <button
                  onClick={handleLogTime}
                  style={{
                    alignSelf: 'flex-end', padding: '7px 16px', borderRadius: 8,
                    border: 'none', background: NAVY, color: '#fff',
                    fontSize: 13, fontWeight: 700, cursor: 'pointer',
                  }}
                >
                  Log
                </button>
              </div>

              {/* Entries */}
              {(task.timeTracked || []).length === 0 ? (
                <p style={{ color: '#bbb', fontSize: 13, textAlign: 'center' }}>No time logged yet.</p>
              ) : (
                [...(task.timeTracked || [])].reverse().map((entry, i) => {
                  const u = rosterMap.get(entry.userId) || { name: 'Former member', color: '#9ca3af' };
                  return (
                    <div
                      key={i}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 10,
                        padding: '8px 12px', marginBottom: 4,
                        borderRadius: 8, border: `1px solid ${BORDER}`, background: '#fff',
                      }}
                    >
                      {u && <Avatar userId={u.id} size={24} />}
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, color: '#333' }}>{entry.note || '—'}</div>
                        <div style={{ fontSize: 11, color: '#aaa' }}>{u?.name} · {entry.date}</div>
                      </div>
                      <span style={{ fontSize: 13, fontWeight: 700, color: NAVY }}>{formatTime(entry.minutes)}</span>
                    </div>
                  );
                })
              )}
            </div>
          )}

          {/* ── FILES ── */}
          {tab === 'files' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {attachments.map(a => (
                <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderRadius: 8, border: `1px solid ${BORDER}`, background: '#fff' }}>
                  <FileIcon source={a.source} mimeType={a.mimeType} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#222', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.fileName}</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      {a.source && a.source !== 'upload' && (
                        <span style={{ fontSize: 10, fontWeight: 700, color: '#fff', background: a.source === 'google_drive' ? '#4285f4' : a.source === 'onedrive' ? '#0078d4' : '#888', borderRadius: 3, padding: '1px 5px' }}>
                          {a.source === 'google_drive' ? 'Google Drive' : a.source === 'onedrive' ? 'OneDrive' : 'Link'}
                        </span>
                      )}
                      {a.fileSize && <span style={{ fontSize: 11, color: '#aaa' }}>{(a.fileSize / 1024).toFixed(0)} KB</span>}
                    </div>
                  </div>
                  {a.storageKey && !a.externalUrl ? (
                    <button type="button" onClick={() => openAttachment(a)} style={{ fontSize: 12, color: '#5352ed', fontWeight: 700, textDecoration: 'none', whiteSpace: 'nowrap', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                      Download
                    </button>
                  ) : (
                    <a href={a.externalUrl || a.fileUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, color: '#5352ed', fontWeight: 700, textDecoration: 'none', whiteSpace: 'nowrap' }}>
                      {a.source && a.source !== 'upload' ? 'Open' : 'Download'}
                    </a>
                  )}
                  <button onClick={() => removeAttachment(a.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#bbb', fontSize: 16, lineHeight: 1, padding: 2 }} title="Remove">×</button>
                </div>
              ))}
              {attachments.length === 0 && !addingFile && !linkingFile && (
                <p style={{ color: '#aaa', fontSize: 13, margin: 0 }}>No files attached yet.</p>
              )}
              {addingFile && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '10px 12px', borderRadius: 8, border: `1px dashed ${BORDER}`, background: BG }}>
                  <input value={newFileName} onChange={e => setNewFileName(e.target.value)} placeholder="File name (e.g. Brief v2.pdf)" style={{ border: `1px solid ${BORDER}`, borderRadius: 6, padding: '6px 10px', fontSize: 13, outline: 'none', fontFamily: 'inherit' }} />
                  <input value={newFileUrl} onChange={e => setNewFileUrl(e.target.value)} placeholder="File URL (S3, Dropbox…)" style={{ border: `1px solid ${BORDER}`, borderRadius: 6, padding: '6px 10px', fontSize: 13, outline: 'none', fontFamily: 'inherit' }} />
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={addAttachment} disabled={savingFile || !newFileName.trim() || !newFileUrl.trim()} style={{ background: NAVY, color: '#fff', border: 'none', borderRadius: 6, padding: '6px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer', opacity: savingFile ? 0.7 : 1 }}>
                      {savingFile ? 'Saving…' : 'Save'}
                    </button>
                    <button onClick={() => { setAddingFile(false); setNewFileName(''); setNewFileUrl(''); }} style={{ background: 'none', border: `1px solid ${BORDER}`, borderRadius: 6, padding: '6px 12px', fontSize: 12, cursor: 'pointer' }}>Cancel</button>
                  </div>
                </div>
              )}
              {linkingFile && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '10px 12px', borderRadius: 8, border: `1px dashed #4285f4`, background: '#f0f4ff' }}>
                  <select value={linkSource} onChange={e => setLinkSource(e.target.value)} style={{ border: `1px solid ${BORDER}`, borderRadius: 6, padding: '6px 10px', fontSize: 13, outline: 'none', fontFamily: 'inherit', background: '#fff' }}>
                    <option value="google_drive">Google Drive</option>
                    <option value="onedrive">OneDrive</option>
                    <option value="url">Other URL</option>
                  </select>
                  <input value={linkName} onChange={e => setLinkName(e.target.value)} placeholder="Display name (e.g. Brand Guidelines)" style={{ border: `1px solid ${BORDER}`, borderRadius: 6, padding: '6px 10px', fontSize: 13, outline: 'none', fontFamily: 'inherit' }} />
                  <input value={linkUrl} onChange={e => setLinkUrl(e.target.value)} placeholder="Paste link (must start with https://)" style={{ border: `1px solid ${BORDER}`, borderRadius: 6, padding: '6px 10px', fontSize: 13, outline: 'none', fontFamily: 'inherit' }} />
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={addLink} disabled={savingLink || !linkName.trim() || !linkUrl.trim()} style={{ background: '#4285f4', color: '#fff', border: 'none', borderRadius: 6, padding: '6px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer', opacity: savingLink ? 0.7 : 1 }}>
                      {savingLink ? 'Linking…' : 'Link'}
                    </button>
                    <button onClick={() => { setLinkingFile(false); setLinkName(''); setLinkUrl(''); }} style={{ background: 'none', border: `1px solid ${BORDER}`, borderRadius: 6, padding: '6px 12px', fontSize: 12, cursor: 'pointer' }}>Cancel</button>
                  </div>
                </div>
              )}
              {!addingFile && !linkingFile && (
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={() => setAddingFile(true)} style={{ background: BG, border: `1px solid ${BORDER}`, borderRadius: 7, padding: '7px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer', color: NAVY }}>
                    + Attach file
                  </button>
                  <button onClick={() => setLinkingFile(true)} style={{ background: '#f0f4ff', border: `1px solid #c5d5f5`, borderRadius: 7, padding: '7px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer', color: '#4285f4' }}>
                    📎 Link file
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

function StatItem({ label, value }) {
  return (
    <div style={{ flex: 1, textAlign: 'center' }}>
      <div style={{ fontSize: 16, fontWeight: 800, color: NAVY }}>{value}</div>
      <div style={{ fontSize: 10, color: '#999', marginTop: 1 }}>{label}</div>
    </div>
  );
}
